import React from "react";
import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { Heart, Truck } from "@phosphor-icons/react";
import { useFavorites } from "@/context/FavoritesContext";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export const formatPrice = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);

export default function ProductCard({ product }) {
  const { isFav, toggle } = useFavorites();
  const path = product.main_image || (product.images && product.images[0]);
  const img = path ? fileUrl(path) : PLACEHOLDER;
  const freeShipping = !!product.free_shipping;
  const soldOut = product.in_stock === false || product.is_sold_out;
  const fav = isFav(product.id);

  return (
    <Link
      to={`/producto/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="meli-card group flex flex-col overflow-hidden relative"
    >
      <button onClick={(e) => { e.preventDefault(); toggle(product.id); }} data-testid={`fav-${product.id}`}
              className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:scale-110 transition-transform">
        <Heart weight={fav ? "fill" : "regular"} size={17} className={fav ? "text-red-500" : "text-gray-500"} />
      </button>
      {product.discount_percent > 0 && (
        <span className="absolute top-2 left-2 z-10 text-[11px] font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--meli-green))] text-white">
          -{product.discount_percent}%
        </span>
      )}
      {product.is_adult && (
        <span className="z-10 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white absolute left-2" style={{ top: product.discount_percent > 0 ? "2.1rem" : "0.5rem" }}>+18</span>
      )}
      {soldOut && (
        <span className="absolute bottom-[46%] left-2 z-10 text-[10px] font-bold px-1.5 py-0.5 rounded bg-foreground text-white uppercase">
          Agotado
        </span>
      )}
      <div className="overflow-hidden bg-white aspect-square p-3">
        <img
          src={img}
          alt={product.title}
          className={`w-full h-full object-cover rounded-md group-hover:scale-[1.03] transition-transform duration-300 ${soldOut ? "opacity-60" : ""}`}
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
        />
      </div>
      <div className="px-4 pb-4 pt-1 flex flex-col gap-0.5 flex-1">
        {product.compare_at_price > product.price && (
          <span className="text-xs text-muted-foreground line-through">{formatPrice(product.compare_at_price)}</span>
        )}
        <span className="font-bold text-2xl text-foreground tracking-tight leading-none">{formatPrice(product.price)}</span>
        {product.discount_percent > 0 && (
          <span className="text-[13px] font-semibold text-[hsl(var(--meli-green))]">{product.discount_percent}% OFF</span>
        )}
        {freeShipping && (
          <span className="text-[13px] font-semibold text-[hsl(var(--meli-green))] flex items-center gap-1"><Truck weight="bold" size={14} /> Envío gratis</span>
        )}
        <h3 className="text-sm text-foreground/80 leading-snug line-clamp-2 mt-1">{product.title}</h3>
        <div className="mt-auto pt-2 flex items-center gap-2 flex-wrap">
          {(product.category_names || []).slice(0, 1).map((c) => (
            <span key={c} className="text-[11px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{c}</span>
          ))}
          {product.variants && product.variants.length > 0 && (
            <span className="text-[11px] text-primary font-semibold">{product.variants.length} opciones</span>
          )}
        </div>
      </div>
    </Link>
  );
}
